import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRES_AT_KEY, BACKEND_URL, BASE_FETCH_URL } from '../../constants/config';
import queryString from 'query-string';
import { emitSessionExpired } from '../auth/session-expired';
import { redactCollabToken } from '@/shared/utils/redact-collab-token';
import { creeazaRefreshSerializat, lockIntreTaburi } from '../auth/refresh-serializat';
import { trebuieReimprospatat } from '../auth/expirare-token';
import { salveazaTokenDeAcces } from '../auth/stocare-token';

// ============================================================================
// TOKEN REFRESH STATE
// ============================================================================

/**
 * Tracks if a token refresh is currently in progress.
 * Prevents multiple simultaneous refresh attempts.
 */
let isRefreshing = false;

/**
 * Queue of failed requests waiting for token refresh to complete.
 * Each entry contains resolve/reject callbacks and the original request config.
 */
let failedRequestsQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: InternalAxiosRequestConfig;
}> = [];

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

// Create axios instance
// IMPORTANT: baseURL does NOT include /api prefix - you must add it explicitly!
//
// API Pattern Guide:
// ==================
// 1. OpenAPI-Generated Functions (Preferred - 90% of APIs):
//    - Functions like getSchoolsReport(), getUsersReport(), etc.
//    - Endpoints include /api automatically (defined in OpenAPI spec)
//    - Example: getSchoolsReport() → calls /api/schools/report
//
// 2. Manual apiClient Calls (Legacy - for non-generated endpoints):
//    - Direct axios calls: apiClient.get('/api/...'), apiClient.post('/api/...')
//    - You MUST manually add /api prefix to the URL
//    - Example: apiClient.get('/api/files/excel-reports', {...})
//
// 3. Public Endpoints (No auth required):
//    - Use full URL: axios.post(`${BACKEND_URL}/api/auth/login`, {...})
//
// ⚠️ Common Mistake: Forgetting /api prefix in manual calls causes 404 errors!
export const apiClient: AxiosInstance = axios.create({
    baseURL: BACKEND_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: (params) => {
        return queryString.stringify(params, { arrayFormat: 'repeat' as any });
    },
});

// Add auth interceptor with proactive token refresh
apiClient.interceptors.request.use(
    async (config) => {
        // Skip auth for authentication endpoints
        if (config.url?.includes('/auth/login') ||
            config.url?.includes('/auth/register') ||
            config.url?.includes('/auth/refresh-token')) {
            return config;
        }

        // Proactive refresh: Check if token expires soon (Auth0 best practice)
        if (shouldRefreshToken()) {
            try {
                await refreshAccessToken();
            } catch (error) {
                // Refresh failed - will be handled by response interceptor
                console.error('Proactive token refresh failed:', error);
            }
        }

        // Add access token to request
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        // Log request setup errors
        console.error('API Request setup error:', error);
        return Promise.reject(error);
    }
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Processes all queued requests after successful token refresh.
 * Retries each request with the new token.
 */
const processQueue = (error: any = null) => {
    failedRequestsQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            // Retry the request with new token
            promise.resolve(apiClient(promise.config));
        }
    });

    failedRequestsQueue = [];
};

/**
 * Checks if the access token should be refreshed.
 * Returns true if token expires within 60 seconds (proactive refresh).
 * Auth0 best practice: refresh before expiration to prevent 401 errors.
 */
const shouldRefreshToken = (): boolean =>
    // Cheia de expirare lipsa (sesiune de dinainte de 2026-10-07) => se citeste `exp` din token.
    trebuieReimprospatat({
        token: localStorage.getItem(TOKEN_KEY),
        expiraLaStocat: localStorage.getItem(TOKEN_EXPIRES_AT_KEY),
        acum: Date.now(),
    });

/**
 * Refreshes the access token using the refresh token (rotation: the backend consumes the old one).
 *
 * 🔴 SINGURA cale de refresh din aplicatie (2026-09-28) — o foloseste si `handleTokenRefresh` din
 * `auth-provider-v2`. Serializata in tab (o promisiune comuna) si intre taburi (Web Lock), vezi
 * `core/auth/refresh-serializat.ts`: doua cereri cu acelasi token deconectau omul.
 * Returns the new access token or throws an error.
 */
export const refreshAccessToken = creeazaRefreshSerializat({
    citeste: () => ({
        token: localStorage.getItem(TOKEN_KEY),
        refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
    }),
    // Call refresh endpoint directly (don't use apiClient to avoid interceptor loop)
    trimite: async (refreshToken) =>
        (await axios.post(`${BASE_FETCH_URL}/auth/refresh-token`, { refreshToken })).data,
    salveaza: ({ token, refreshToken, expiresIn }) => {
        // CRITICAL: Update BOTH tokens (token rotation)
        // Tokenul de acces si expirarea lui, prin acelasi helper ca la login (`stocare-token.ts`).
        salveazaTokenDeAcces(token, expiresIn);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    },
    cuLock: lockIntreTaburi(),
});

// ============================================================================
// RESPONSE INTERCEPTOR (401 Retry with Token Refresh)
// ============================================================================

/**
 * Response interceptor that handles 401 errors with automatic token refresh.
 *
 * Flow:
 * 1. On 401 error, check if refresh is already in progress
 * 2. If not, attempt token refresh and retry original request
 * 3. If refresh in progress, queue request for retry after refresh completes
 * 4. On successful refresh, retry all queued requests
 * 5. On refresh failure, clear queue and redirect to login
 * 6. For all errors (except 401), log to console
 */
apiClient.interceptors.response.use(
    (response) => {
        // Simply return successful responses
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Check if error is 401 and we haven't already retried
        // Skip token refresh for public auth endpoints (login, register, password reset)
        // These endpoints don't require auth and 401 indicates invalid credentials, not expired token
        // Exception: /api/auth/change-password requires auth, so allow token refresh for it
        const isPublicAuthEndpoint = originalRequest.url?.includes('/api/auth/') &&
                                     !originalRequest.url?.includes('/api/auth/change-password');

        if (error.response?.status === 401 && !originalRequest._retry && !isPublicAuthEndpoint) {
            originalRequest._retry = true;

            if (isRefreshing) {
                // Refresh already in progress, queue this request
                return new Promise((resolve, reject) => {
                    failedRequestsQueue.push({
                        resolve,
                        reject,
                        config: originalRequest,
                    });
                });
            }

            isRefreshing = true;

            try {
                // Attempt to refresh the token
                const newToken = await refreshAccessToken();

                // Update authorization header with new token
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }

                // Process all queued requests with the new token
                processQueue();

                // Retry the original request
                return apiClient(originalRequest);
            } catch (refreshError) {
                // Refresh failed, reject all queued requests
                processQueue(refreshError);

                // Clear ALL tokens, then tell the auth provider — it does the real logout
                // (React state + navigate to login). Nothing else navigates from here.
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
                localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);

                // User-friendly message for expired refresh tokens
                if (axios.isAxiosError(refreshError) &&
                    refreshError.response?.data?.message?.includes('refresh.token')) {
                    console.info('Session expired - please login again');
                }

                // 2026-08-28: comentariul de aici spunea ca navigarea o face „storage listener-ul
                // din auth-provider-v2 (line 218)". Acel listener nu a existat niciodata, iar
                // `storage` nu se declanseaza in tab-ul care scrie. Semnalul explicit de mai jos
                // e ascultat de AuthProviderV2, care apeleaza logout().
                emitSessionExpired();

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Not a 401 or already retried, log and pass through
        const status = error.response?.status;
        // Tokenul de colaborator sta in path (/api/collab/<token>/...), nu intr-un header - un
        // 413/500/cadere de retea l-ar duce direct in consola, la fiecare cerere esuata.
        const url = redactCollabToken(error.config?.url ?? '');
        const method = error.config?.method;

        // Log errors that aren't 401 (auth errors are expected/handled)
        // and aren't AbortErrors (cancelled requests from React Router navigation)
        if (status !== 401 && error.name !== 'AbortError') {
            console.error(
                'API Error:',
                `[${status} ${method?.toUpperCase()} ${url}]`,
                error.message
            );
        }

        return Promise.reject(error);
    }
);

// Export typed request methods with automatic data unwrapping
// This is for backward compatibility with old code that expects data directly
export const api = {
    get: <T>(url: string, config?: any): Promise<T> =>
        apiClient.get(url, config).then(res => res.data),
    post: <T>(url: string, data?: any, config?: any): Promise<T> =>
        apiClient.post(url, data, config).then(res => res.data),
    put: <T>(url: string, data?: any, config?: any): Promise<T> =>
        apiClient.put(url, data, config).then(res => res.data),
    patch: <T>(url: string, data?: any, config?: any): Promise<T> =>
        apiClient.patch(url, data, config).then(res => res.data),
    delete: <T>(url: string, config?: any): Promise<T> =>
        apiClient.delete(url, config).then(res => res.data),
};